// External libraries
import React from 'react';

// Internal
import { Checkbox } from '@/components/Common';
import type { InputOnChange } from '@/types';
import { getContent, getLocale } from '@/lib';
import { useStoreState } from '@/store';
import { THIRD_PARTY_DATA_CONSENT } from '@/lib/features/constants';

// Legacy
// @ts-expect-error untyped-import
import { FieldError } from '@checkout/react-components';
import { errorStyleOverrides } from '../PrivacyStatement/StyledComponents';

type ThirdPartyDataConsentCheckboxProps = {
  changeHandler: InputOnChange;
};

/**
 * The party data consent checkbox is required for KR users.
 * Related feature flag: hasThirdPartyDataConsentCheckbox
 * Related ELMO: FEATURE_THIRD_PARTY_DATA_CONSENT
 * Related JIRA Epic: https://paypal.atlassian.net/browse/DTXOCOMPL-1687
 */
export const ThirdPartyDataConsentCheckbox = ({
  changeHandler,
}: ThirdPartyDataConsentCheckboxProps): JSX.Element => {
  const isFormSubmitting = useStoreState(({ ui }) => ui.isFormSubmitting);
  const thirdPartyDataConsent = useStoreState(
    ({ form }) => form[THIRD_PARTY_DATA_CONSENT],
  );
  const hasError = Boolean(thirdPartyDataConsent?.displayError);

  return (
    <div>
      <Checkbox
        aria-invalid={hasError}
        changeHandler={changeHandler}
        checked={thirdPartyDataConsent?.value}
        disabled={isFormSubmitting}
        id={THIRD_PARTY_DATA_CONSENT}
        name={THIRD_PARTY_DATA_CONSENT}
        isTermsCheckbox
        label={getContent(
          'terms.thirdPartyDataSharing',
          /* The link embedded in 'terms.thirdPartyDataSharing' expects a "lang" variable
      to correctly set the language for the user */
          { lang: getLocale().language },
          /* A link is embedded in the content key 'terms.thirdPartyDataSharing'
      and will break without the useHTML flag */
          { useHTML: true },
        )}
        required={true}
        {...thirdPartyDataConsent}
      />
      {hasError && (
        <FieldError
          id={`${THIRD_PARTY_DATA_CONSENT}-error`}
          styleOverrides={errorStyleOverrides}
        >
          {getContent('fieldErrors.common.valueMissing')}
        </FieldError>
      )}
    </div>
  );
};
